<template>
  <div class="space-y-6">
    <!-- services grid -->
    <div
    v-if="!selectedService"
      class="grid gap-4 gap-y-8"
      :class="{
        'grid-cols-4 md:grid-cols-5': $attrs.grid,
        'grid-cols-3 md:grid-cols-4': !$attrs.grid
      }"
    >
      <div
        v-for="service in sortedServices"
        :key="service.id"
        v-show="!$attrs.group || $attrs.group == service.group"
        class="flex flex-col items-center"
        :class="{
          'grayscale opacity-50 pointer-events-none':
            !isServiceAnyOptionAvailable(service)
        }"
      >
        <!-- circular service button -->
        <div
          @click="selectService(service)"
          class="w-full aspect-square max-w-[80px] rounded-full bg-primary/10 p-4 flex items-center justify-center ripple cursor-pointer transition-transform hover:scale-105 relative overflow-hidden"
          :style="{ backgroundColor: service.color + '20' }"
        >
          <!-- inner colored circle -->
          <div
            class="w-full h-full flex items-center justify-center rounded-2xl"
            :style="{ backgroundColor: service.color }"
          >
            <ImgComponent
              :src="service.logo"
              class="w-3/4"
              alt=""
            />
          </div>
        </div>

        <!-- service name -->
        <div class="pt-2 text-center text-sm font-semibold text-gray-900">
          {{ service.name }}
        </div>
      </div>
    </div>

    <!-- service options (shown when service selected) -->
    <div v-if="selectedService" class="space-y-2 pt-4">
      <div
        v-for="option in $constants.SERVICES.SERVICES_OPTIONS[selectedService.id] || selectedService.options"
        :key="option.id"
        v-show="!$attrs.option || $attrs.option == option.id"
      >
        <component
          :is="option.url ? 'a' : 'div'"
          target="_blank"
          :href="option.url"
          @click="
            !option.url &&
              $bus.emit($constants.EVENTS.SERVICES.OPTIONS.SELECTED, option)
          "
          class="flex items-center gap-4 px-4 py-4 rounded-xl ripple cursor-pointer transition-all"
          :class="{
            'bg-primary/10': selectedOption === option.id,
            'bg-white hover:bg-gray-50': selectedOption !== option.id,
            'grayscale opacity-50 pointer-events-none': !isOptionAvailable(selectedService.id, option.id)
          }"
          @click.native="selectedOption = option.id"
        >
          <!-- colored icon -->
          <div class="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            :style="{ backgroundColor: selectedService.color }"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path v-if="option.id === 'fund'" d="M12 19V5M5 12l7-7 7 7"/>
              <path v-else-if="option.id === 'withdraw'" d="M12 5v14M5 12l7 7 7-7"/>
              <path v-else-if="option.id === 'buy'" d="M12 19V5M5 12l7-7 7 7"/>
              <path v-else-if="option.id === 'sell'" d="M12 5v14M5 12l7 7 7-7"/>
              <rect v-else x="3" y="3" width="18" height="18" rx="2"/>
            </svg>
          </div>

          <!-- text -->
          <div class="flex-1 min-w-0">
            <div class="font-semibold text-gray-900">
              {{ option.title }}
            </div>
            <div class="text-sm text-gray-500" v-if="!$attrs.hideDesc">
              {{ option.smallDescription }}
            </div>
          </div>

          <!-- arrow -->
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </component>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getStore } from '@core/store';
import { computed, getCurrentInstance, ref } from 'vue';

const { proxy } = getCurrentInstance();
const userStore = getStore('user');
const userCountry = computed(() => userStore.state.current?.countryCode);

const selectedService = ref(null);
const selectedOption = ref(null);
const sortedServices = computed(() => {
  return [...proxy.$constants.SERVICES.SERVICES_LIST];
});

function selectService(service) {
  selectedService.value = service;
  selectedOption.value = null;
}

const isOptionAvailable = (serviceId, operationId) => {
  const countryCode = userCountry.value;
  const appData = proxy.$stores.appData.state.data;
  const availability = appData?.serviceAvailability?.[countryCode];
  if (!availability) return true;

  const operationKeys = {
    'fund:1xbet': 'fund1xBet',
    'withdraw:1xbet': 'withdrawal1xBet',
    'fund:betwinner': 'fundBetWinner',
    'withdraw:betwinner': 'withdrawalBetWinner',
    'fund:1win': 'fund1Win',
    'withdraw:1win': 'withdrawal1Win',
    'fund:melbet': 'fundMelbet',
    'withdraw:melbet': 'withdrawalMelbet',
    'fund:eightStarz': 'fundEightStarz',
    'withdraw:eightStarz': 'withdrawalEightStarz',
    'buy:usdt': 'buyUsdt',
    'sell:usdt': 'sellUsdt'
  };
  
  const key = `${operationId}:${serviceId}`;
  const opKey = operationKeys[key];
  if (!opKey) return true;
  
  return availability[opKey] ?? true;
};

const isServiceAnyOptionAvailable = (service) => {
  const countryCode = userCountry.value;
  const appData = proxy.$stores.appData.state.data;
  const availability = appData?.serviceAvailability?.[countryCode];
  if (!availability) return true;

  if (service.id === '1xbet') return availability.fund1xBet || availability.withdrawal1xBet;
  if (service.id === 'betwinner') return availability.fundBetWinner || availability.withdrawalBetWinner;
  if (service.id === '1win') return availability.fund1Win || availability.withdrawal1Win;
  if (service.id === 'melbet') return availability.fundMelbet || availability.withdrawalMelbet;
  if (service.id === 'eightStarz') return availability.fundEightStarz || availability.withdrawalEightStarz;
  if (service.id === 'usdt') return availability.buyUsdt || availability.sellUsdt;

  return true;
};
</script>
